import { useState, useEffect, useCallback, useRef } from "react";

// Frontend BFF path: nginx rewrites /chat-bff/* to the Next.js proxy, which
// injects the chat service token server-side. Same-origin, no token in the browser.
const STREAM_API_URL = "/chat-bff/drought_chat_stream/";

/**
 * Parse SSE text from a ReadableStream response body.
 * Yields {event, data} objects for each complete SSE message.
 */
async function* parseSSE(reader) {
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE messages are separated by double newlines
    const parts = buffer.split("\n\n");
    // Keep the last partial chunk in the buffer
    buffer = parts.pop() || "";

    for (const part of parts) {
      if (!part.trim()) continue;
      let event = "message";
      let data = "";
      for (const line of part.split("\n")) {
        if (line.startsWith("event: ")) {
          event = line.slice(7).trim();
        } else if (line.startsWith("data: ")) {
          data = line.slice(6);
        }
      }
      if (data) {
        try {
          yield { event, data: JSON.parse(data) };
        } catch {
          // non-JSON data — skip
        }
      }
    }
  }
}

/**
 * Hook to send a single query via the SSE streaming endpoint.
 * Returns the same interface as useAutoChat for easy swapping.
 */
export default function useStreamChat(query) {
  const [response, setResponse] = useState("");
  const [chartData, setChartData] = useState({});
  const [mapImages, setMapImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const controllerRef = useRef(null);

  const startStream = useCallback(
    async (message, { conversationId, sessionToken, contextData } = {}) => {
      setResponse("");
      setChartData({});
      setMapImages([]);
      setError(null);
      setIsLoading(true);
      setProgress(null);

      const controller = new AbortController();
      controllerRef.current = controller;

      const payload = { message };
      if (conversationId) payload.conversation_id = conversationId;
      if (sessionToken) payload.session_token = sessionToken;
      if (contextData) payload.context_data = contextData;

      try {
        const res = await fetch(STREAM_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!res.ok) {
          let detail = "Failed to start streaming.";
          try {
            const errData = await res.json();
            detail = errData.detail || JSON.stringify(errData);
          } catch (_) {}
          throw new Error(detail);
        }

        const reader = res.body.getReader();
        let accumulated = "";
        let toolOutputs = {};
        let meta = {};

        for await (const { event, data } of parseSSE(reader)) {
          if (controller.signal.aborted) break;

          switch (event) {
            case "progress":
              setProgress(data);
              break;

            case "tool_data": {
              const newData = extractChartData(data.tool_outputs || []);
              const newMaps = extractMapImages(data.tool_outputs || []);
              // Merge with existing chart data (Phase B enrichment adds new keys)
              toolOutputs = { ...toolOutputs, ...newData };
              setChartData(toolOutputs);
              if (newMaps.length > 0) setMapImages(newMaps);
              break;
            }

            case "token":
              accumulated += data.text || "";
              setResponse(accumulated);
              break;

            case "done":
              meta = data;
              break;

            case "error":
              throw new Error(data.message || "Stream error");

            default:
              break;
          }
        }

        setIsLoading(false);
        setProgress(null);
        return { response: accumulated, chartData: toolOutputs, meta };
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Something went wrong.");
          setIsLoading(false);
        }
        return null;
      }
    },
    []
  );

  // Auto-fire the query on mount (like useAutoChat)
  useEffect(() => {
    if (!query) return;
    startStream(query);
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, [query, startStream]);

  return { response, chartData, mapImages, isLoading, error, progress, startStream };
}

function extractChartData(toolOutputs) {
  if (!toolOutputs || toolOutputs.length === 0) return {};
  for (const output of toolOutputs) {
    const cd = output?.result?.chart_data;
    if (cd) return cd;
  }
  return {};
}

function extractMapImages(toolOutputs) {
  if (!toolOutputs || toolOutputs.length === 0) return [];
  for (const output of toolOutputs) {
    const imgs = output?.result?.map_images;
    if (imgs && imgs.length > 0) return imgs;
  }
  return [];
}
